import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  responseErrorsResolver,
  ResponseError,
  ButtonPrimary,
  ButtonSecondary,
  Popup,
  PermissionDenied,
  PermissionEnum,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useCreatePSPExternalAccountMutation,
  useLazyGetPSPExternalAccountsListQuery,
  useLazyGetConfigurationAccountBalanceQuery,
  useCreatePspExternalAccountWithIntegrationMutation,
} from './store/services';
import {
  ExternalAccount,
  ExternalAccountFormValues,
  Balance,
  ExternalAccountWithIntegrationFormValues,
} from './types';
import ExternalAccountCreateForm from '../../components/bank-and-psp/create-external';
import useGetAccountsAutocompleteList from './helpers/use-get-accounts';
import { BankRoutes } from '../../routes';
import { setOpenPayNotificationVisible } from './store/slices/info';

const defaultFormValues = {
  pspPartnerId: '',
  accountId: '',
  iban: '',
  paymentType: '',
  currencyId: [],
  status: 'enabled',
};

const CreateExternalAccount = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [error, setErrorValue] = useState('');
  const [balances, setBalances] = useState<Balance[]>([]);
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [isKeel, setIsKeel] = useState(false);
  const [isPrimeVault, setIsPrimeVault] = useState(false);
  const [integrationType, setIntegrationType] = useState<
    'with-integration' | 'without-integration'
  >('without-integration');

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    configurationDictionaries: { paymentTypes, activePermissions },
    bankAndPsp: {
      info: { takeCount, generalInfo, casp },
    },
  } = useAppSelector((state) => state);

  const [createExternalAccount, { isLoading, isSuccess }] =
    useCreatePSPExternalAccountMutation();

  const [
    getConfigurationBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetConfigurationAccountBalanceQuery();
  const [getExternalAccountsList] = useLazyGetPSPExternalAccountsListQuery();
  const [
    createExternalAccountWithIntegration,
    { isLoading: createInProgress, isSuccess: createIsSuccess },
  ] = useCreatePspExternalAccountWithIntegrationMutation();

  const {
    autocompleteList: accounts,
    onSearch: onSearchAccounts,
    onClear,
    isLoading: accountsIsLoading,
  } = useGetAccountsAutocompleteList('&isClientAccount=true');

  const {
    reset,
    control,
    formState: { errors, isDirty },
    setError,
    handleSubmit,
    setValue,
    watch,
  } = useForm<ExternalAccountFormValues>({
    defaultValues: defaultFormValues,
  });

  const { bankId } = useParams();

  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const accountId = watch('accountId');
  const caspId = watch('caspId');

  const prevCaspId = usePrevious(caspId);
  const prevPaymentType = usePrevious(paymentType);
  const prevAccountId = usePrevious(accountId);

  const onSubmit = handleSubmit(async (values) => {
    setErrorValue('');

    const body: ExternalAccountFormValues = {
      pspPartnerId: bankId as string,
      iban: values.iban,
      paymentType: values.paymentType,
      currencyId: values.currencyId,
      currenciesIds: values.currencyId,
      active: Boolean(values.status === 'enabled'),
    };

    if (values.accountId) {
      body.accountId = values.accountId;
    }

    if (values?.caspId) {
      body.caspId = values.caspId;
    }

    const response = (await createExternalAccount(body)) as {
      error: ResponseError | { data: { message: string } };
      data?: { data: ExternalAccount };
    };

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setErrorValue,
        });
      } else {
        getExternalAccountsList(
          `?take=${takeCount}&pspPartnerId=${bankId}${
            search ? search.replace('?', '&') : ''
          }`,
        );
      }
    }
  });

  const onSubmitWithIntegration = handleSubmit(async (values) => {
    setErrorValue('');

    const body: ExternalAccountWithIntegrationFormValues = {
      accountsIds: [values.accountId as string],
      integrationId: generalInfo?.pspIntegration?.id as string,
      currenciesIds: values.currencyId,
    };

    if (values?.caspId) {
      body.caspId = values.caspId;
    }

    const response = (await createExternalAccountWithIntegration(body)) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: ExternalAccount };
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setErrorValue,
      });
    }
  });

  const handleClose = () => {
    reset(defaultFormValues);

    setTimeout(() => {
      navigate(
        `${generatePath(BankRoutes.BANK_AND_PSP_EXTERNAL, {
          bankId: bankId || '',
        })}${search || ''}`,
      );
    }, 100);
  };

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getConfigurationBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  useEffect(() => {
    if (generalInfo && generalInfo?.pspIntegration) {
      if (generalInfo?.pspIntegration?.name === 'OPEN_PAYD') {
        setIsOpenPayd(true);
      }

      if (generalInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET') {
        setIsPrimeVault(true);
      }

      if (generalInfo?.pspIntegration?.name === 'Keel') {
        setIsKeel(true);
      }
    }
  }, [generalInfo]);

  useEffect(() => {
    if (
      generalInfo &&
      generalInfo?.pspIntegration &&
      generalInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET'
    ) {
      setIsPrimeVault(true);
    }
  }, [generalInfo]);

  useEffect(() => {
    if (accountId) {
      getAccountsBalancesAction(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (
      (((!isPrimeVault && !isOpenPayd && !isKeel) ||
        (isPrimeVault && integrationType === 'without-integration') ||
        ((isOpenPayd || isKeel) &&
          integrationType === 'without-integration')) &&
        paymentType !== prevPaymentType) ||
      (isPrimeVault &&
        integrationType === 'with-integration' &&
        caspId !== prevCaspId) ||
      accountId !== prevAccountId
    ) {
      setValue('currencyId', []);
    }
  }, [
    isPrimeVault,
    isOpenPayd,
    isKeel,
    integrationType,
    prevCaspId,
    prevPaymentType,
    caspId,
    paymentType,
    accountId,
    prevAccountId,
  ]);

  useEffect(() => {
    if (isSuccess || createIsSuccess) {
      handleClose();

      if (createIsSuccess) {
        dispatch(setOpenPayNotificationVisible(true));

        setTimeout(() => {
          dispatch(setOpenPayNotificationVisible(false));
        }, 10000);
      }
    }
  }, [isSuccess, createIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content = <div />;

  if (activePermissions) {
    if (
      activePermissions.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
      )
    ) {
      content = (
        <ExternalAccountCreateForm
          control={control}
          errors={errors}
          paymentTypes={paymentTypes}
          paymentType={paymentType}
          selectedPartner={generalInfo}
          setValue={setValue}
          currencyId={currencyId}
          accounts={accounts}
          onSearchAccounts={onSearchAccounts}
          onClear={onClear}
          accountsIsLoading={accountsIsLoading}
          balances={balances}
          casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
          caspValues={casp}
          isOpenPayd={isOpenPayd}
          isKeel={isKeel}
          integrationType={integrationType}
          setIntegrationType={setIntegrationType}
          openPaydIntegration={
            isOpenPayd || isKeel ? generalInfo?.pspIntegration : undefined
          }
          accountId={accountId}
          balancesIsLoading={balancesIsLoading || balancesIsFetching}
          isPrimeVault={isPrimeVault}
          caspId={caspId}
        />
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Add External Account"
        sidepanelExpandedKey="bank-create-external"
        errorSidepanel={error}
        footer={
          <>
            <ButtonPrimary
              text={
                isLoading || createInProgress ? 'Loading...' : 'Add account'
              }
              disabled={!isDirty || isLoading || createInProgress}
              onClick={() =>
                integrationType === 'with-integration'
                  ? onSubmitWithIntegration()
                  : onSubmit()
              }
              bold
            />
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateExternalAccount;
